<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import PageHeader from '@lib/components/shared/PageHeader.vue'
import { useUiStore } from '@/stores/ui'
import { useToast } from '@lib/composables/useToast'
import { useStatisticsData } from '@/composables/useStatisticsData'
import { defaultFilters, type StatsFilters } from '@/components/domain/statistics/statistics-filters'
import StatisticsFilterBar from '@/components/domain/statistics/StatisticsFilterBar.vue'
import StatisticsKpiCards from '@/components/domain/statistics/StatisticsKpiCards.vue'
import StatisticsTable from '@/components/domain/statistics/StatisticsTable.vue'
import StatisticsChartCard from '@/components/domain/statistics/StatisticsChartCard.vue'

const { t } = useI18n()
const ui = useUiStore()
const toast = useToast()
const data = useStatisticsData()

const filters = ref<StatsFilters>(defaultFilters())

onMounted(() => {
    ui.setBreadcrumbs([{ label: t('statistics.title') }])
})

async function onApply() {
    await data.fetch(filters.value)
    if (data.error.value) {
        toast.error(t('statistics.toast.error'), data.error.value)
    }
}
</script>

<template>
    <DefaultLayout>
        <PageHeader :title="t('statistics.title')" />

        <StatisticsFilterBar v-model="filters" :loading="data.loading.value" @apply="onApply" />

        <!-- Before first fetch -->
        <div
            v-if="data.stats.value === null && !data.loading.value && !data.error.value"
            class="bg-white border border-gray-200 rounded-12 p-12 text-center text-sm text-gray-500"
        >
            {{ t('statistics.prompt.beforeFirstFetch') }}
        </div>

        <!-- After fetch -->
        <template v-else-if="data.stats.value">
            <StatisticsKpiCards
                :total="data.stats.value.ticketsTotal"
                :validated="data.stats.value.ticketsValidated"
            />

            <!-- Zero results -->
            <div
                v-if="data.stats.value.ticketsTotal === 0"
                class="bg-white border border-gray-200 rounded-12 p-8 text-center text-sm text-gray-500"
            >
                {{ t('statistics.prompt.noResults') }}
            </div>

            <!-- Table -->
            <StatisticsTable
                v-else
                :rows="data.stats.value.ticketsetSums"
                :total="data.stats.value.ticketsetSumsTotal"
            />

            <!-- Charts -->
            <template v-if="data.stats.value.ticketsTotal > 0">
                <div class="text-sm font-semibold text-ink mb-3">{{ t('statistics.charts.title') }}</div>
                <div
                    v-if="data.stats.value.device.length === 0 || data.stats.value.labels.length === 0"
                    class="bg-white border border-gray-200 rounded-12 p-8 text-center text-sm text-gray-500"
                >
                    {{ t('statistics.charts.empty') }}
                </div>
                <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <StatisticsChartCard
                        v-for="d in data.stats.value.device"
                        :key="d.id"
                        :name="d.name || d.username"
                        :count="d.count"
                        :labels="data.stats.value.labels"
                        :values="d.values"
                    />
                </div>
            </template>
        </template>
    </DefaultLayout>
</template>
